import React from 'react';
import { Grid, Stack, Typography } from '@mui/material';
import { CategoryCell } from '../Table/components/Cells/Cells';
import { MoreCategoriesTooltip } from './MoreCategoriesTooltip';

interface CategoryListProps {
  categories: string[];
  maxVisible?: number;
  filter?: boolean;
  /**
   * Which taxonomy `categories` holds (VRG-628). Defaults to `'category'`, so this
   * stays a no-op for any existing caller.
   *
   * Pass `'productType'` when the strings are PRODUCT TYPES — the tag shop's
   * `best_for_product_types`, 68 of them. They then resolve through
   * `getProductTypeIcon` instead of the `icons` map inside `CategoryCell`, where 14
   * keys double as product-CATEGORY slugs and a rename on one axis silently
   * repaints the other.
   */
  kind?: 'category' | 'productType';
}

export const CategoryList: React.FC<CategoryListProps> = ({
  categories,
  maxVisible = 5,
  filter = false,
  kind = 'category',
}) => {
  if (!categories || categories.length === 0) {
    return (
      <Grid item sx={{ mr: 2, mt: 2 }}>
        <Typography variant="body3" color="text.secondary">
          No categories specified
        </Typography>
      </Grid>
    );
  }

  const visibleCategories = categories.slice(0, maxVisible);
  const hiddenCategories = categories.slice(maxVisible);
  const hasMore = hiddenCategories.length > 0;

  return (
    <>
      {visibleCategories.map((category, index) => (
        <Grid item key={index} sx={{ mr: 2, mt: 2 }}>
          <CategoryCell category={category} filter={filter} kind={kind} />
        </Grid>
      ))}

      {hasMore && (
        <Grid item sx={{ mr: 2, mt: 2 }}>
          {/* Overflow peek — shared with the tag-shop card so both render identically. */}
          <MoreCategoriesTooltip categories={hiddenCategories} filter={filter} kind={kind}>
            <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '100%' }}>
              <Typography
                variant="body3"
                noWrap
                sx={{
                  cursor: 'default',
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                + {hiddenCategories.length} more
              </Typography>
            </Stack>
          </MoreCategoriesTooltip>
        </Grid>
      )}
    </>
  );
};
